"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "../../enterprise-transformation/sap-s4hana/Icon";

type ResourceType = "Playbook" | "Guide" | "Template" | "Checklist" | "Whitepaper" | "Framework";
type Topic = "data" | "analytics" | "ai" | "sap" | "supply" | "finance" | "customer" | "esg";

type Resource = {
  type: ResourceType;
  topic: Topic;
  title: string;
  text: string;
  meta: string;
  image: string;
  date: string; // ISO — used for "Latest" sorting
  featured?: boolean;
};

const resources: Resource[] = [
  { type: "Playbook", topic: "data", title: "Building an AI-ready Data Foundation", text: "A step-by-step playbook to prepare your enterprise data for AI and advanced analytics.", meta: "12 pages | PDF", image: "/assets/images/Building-an-AI-ready-Data-Foundation.jpg", date: "2026-09-16", featured: true },
  { type: "Guide", topic: "sap", title: "SAP Data for Business Insights", text: "A practical guide to unlock value from SAP data across finance, supply chain and sales.", meta: "10 pages | PDF", image: "/assets/images/SAP-Data-for-Business-Insights.jpg", date: "2026-09-14", featured: true },
  { type: "Template", topic: "ai", title: "Use Case Prioritisation Template", text: "A ready-to-use template to evaluate and prioritise data and AI use cases.", meta: "XLSX | Editable", image: "/assets/images/Use-Case-Prioritisation-Template.jpg", date: "2026-09-12", featured: true },
  { type: "Checklist", topic: "data", title: "Data Governance Readiness Checklist", text: "A checklist to assess your current data governance maturity and next steps.", meta: "6 pages | PDF", image: "/assets/images/Data-Governance-Readiness-Checklist.jpg", date: "2026-09-10", featured: true },
  { type: "Whitepaper", topic: "analytics", title: "From Data to Decisions: The Analytics Journey", text: "Key steps to build a scalable analytics capability.", meta: "14 pages | PDF", image: "/assets/images/whitepaper.jpg", date: "2026-09-08" },
  { type: "Playbook", topic: "supply", title: "Data Use Cases in Manufacturing", text: "A practical playbook to identify and scale high-value use cases.", meta: "12 pages | PDF", image: "/assets/images/manufacturing.jpg", date: "2026-09-05" },
  { type: "Template", topic: "finance", title: "KPI Framework for Enterprise Analytics", text: "A template to define and track the right KPIs for your business.", meta: "XLSX | Editable", image: "/assets/images/data-intelligence.jpg", date: "2026-09-02" },
  { type: "Guide", topic: "ai", title: "A Business Leader's Guide to Generative AI", text: "An easy-to-understand guide to opportunities, risks and next steps.", meta: "8 pages | PDF", image: "/assets/images/the-enterprise-ai-map.jpg", date: "2026-08-30" },
  { type: "Framework", topic: "customer", title: "Customer Analytics Maturity Model", text: "A framework to benchmark how you use customer and sales data today.", meta: "9 pages | PDF", image: "/assets/images/customer.jpg", date: "2026-08-26" },
  { type: "Checklist", topic: "esg", title: "ESG Data Reporting Checklist", text: "A checklist to prepare sustainability data for reliable reporting.", meta: "5 pages | PDF", image: "/assets/images/case-study.jpg", date: "2026-08-22" },
  { type: "Framework", topic: "finance", title: "Risk & Compliance Data Framework", text: "A framework to connect finance, risk and compliance data with confidence.", meta: "11 pages | PDF", image: "/assets/images/Quality-Reviews.jpg", date: "2026-08-18" },
  { type: "Playbook", topic: "sap", title: "Clean Core Adoption Playbook", text: "Practical steps to keep your SAP core standard while extending with confidence.", meta: "13 pages | PDF", image: "/assets/images/modernise-integrate-accelerate.jpg", date: "2026-08-14" },
];

const tabs: { id: string; label: string; icon: string; match: (r: Resource) => boolean }[] = [
  { id: "all", label: "All Resources", icon: "compass", match: () => true },
  { id: "data", label: "Data & Analytics", icon: "chart", match: (r) => r.topic === "data" || r.topic === "analytics" },
  { id: "ai", label: "AI & GenAI", icon: "gear", match: (r) => r.topic === "ai" },
  { id: "sap", label: "SAP & ERP", icon: "database", match: (r) => r.topic === "sap" },
  { id: "playbooks", label: "Industry Playbooks", icon: "factory", match: (r) => r.type === "Playbook" },
  { id: "frameworks", label: "Frameworks & Models", icon: "network", match: (r) => r.type === "Framework" || r.type === "Whitepaper" },
  { id: "checklists", label: "Checklists", icon: "decide", match: (r) => r.type === "Checklist" },
  { id: "templates", label: "Templates", icon: "orders", match: (r) => r.type === "Template" },
];

const relatedLinks: { href: string; label: string; icon: string }[] = [
  { href: "/datasap", label: "Data & SAP", icon: "database" },
  { href: "/whatwedo/Ai-and-Intelligence", label: "AI & Intelligence", icon: "gear" },
  { href: "/enterprise-transformation/sap-s4hana", label: "SAP S/4HANA", icon: "server" },
  { href: "/knowledge/supply-chain", label: "Supply Chain", icon: "truck" },
  { href: "/knowledge/finance-performance", label: "Finance Performance", icon: "finance" },
  { href: "/knowledge/customer&revenue", label: "Customer & Revenue", icon: "people" },
  { href: "/industries", label: "Industries", icon: "factory" },
  { href: "/solutions", label: "Solutions", icon: "chart" },
];

function ResourceCard({ r }: { r: Resource }) {
  return (
    <article className="s4-card s4-use-card pd-card">
      <div className="s4-use-image pd-card-image">
        <img src={r.image} alt={r.title} loading="lazy" />
        <span className="pd-badge">{r.type}</span>
      </div>
      <div className="s4-use-body">
        <h3>{r.title}</h3>
        <p>{r.text}</p>
        <div className="pd-card-foot">
          <span className="pd-meta">{r.meta}</span>
          <a href="#" onClick={(e) => e.preventDefault()} className="s4-learn" aria-label={`Download ${r.title}`}>
            Download <span>→</span>
          </a>
        </div>
      </div>
    </article>
  );
}

export default function ResourceLibrary() {
  const [tab, setTab] = useState("all");
  const [sort, setSort] = useState<"latest" | "title">("latest");

  const featured = resources.filter((r) => r.featured);

  const visible = (() => {
    const activeTab = tabs.find((t) => t.id === tab) ?? tabs[0];
    const list = resources.filter((r) => activeTab.match(r));
    return [...list].sort((a, b) =>
      sort === "title" ? a.title.localeCompare(b.title) : b.date.localeCompare(a.date)
    );
  })();

  const jumpToLibrary = () =>
    document.getElementById("all-resources")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      {/* CATEGORY TABS */}
      <section className="s4-section" id="browse">
        <div className="pd-tabs et-reveal" role="tablist" aria-label="Resource categories">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`pd-tab${tab === t.id ? " is-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <span className="pd-tab-icon"><Icon name={t.icon} /></span>
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="s4-section">
        <div className="s4-heading-row et-reveal">
          <div className="s4-heading">
            <h2>Featured resources</h2>
            <p>Handpicked guides, playbooks and templates to help you take action.</p>
          </div>
          <a href="#all-resources" className="s4-link" onClick={(e) => { e.preventDefault(); setTab("all"); jumpToLibrary(); }}>
            View all resources <span>→</span>
          </a>
        </div>
        <div className="pd-grid et-stagger">
          {featured.map((r) => <ResourceCard r={r} key={r.title} />)}
        </div>
      </section>

      {/* RELATED LINKS */}
      <section className="s4-section">
        <div className="pd-topic-grid et-stagger">
          {relatedLinks.map((l) => (
            <Link key={l.href} href={l.href} className="s4-card pd-topic">
              <span className="s4-icon"><Icon name={l.icon} /></span>
              <strong>{l.label}</strong>
            </Link>
          ))}
        </div>
      </section>

      {/* ALL RESOURCES */}
      <section className="s4-section" id="all-resources">
        <div className="s4-heading-row et-reveal">
          <div className="s4-heading">
            <h2>All resources</h2>
            <p>Explore our complete library of playbooks, guides, templates and more.</p>
          </div>
          <label className="pd-sort">
            <span>Sort by:</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as "latest" | "title")}>
              <option value="latest">Latest</option>
              <option value="title">Title (A–Z)</option>
            </select>
          </label>
        </div>

        {visible.length > 0 ? (
          <div className="pd-grid">
            {visible.map((r) => <ResourceCard r={r} key={r.title} />)}
          </div>
        ) : (
          <p className="pd-empty">
            No resources match your filters.{" "}
            <button type="button" onClick={() => setTab("all")}>
              Clear filters
            </button>
          </p>
        )}
      </section>
    </>
  );
}
